import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { QueryClient } from "@tanstack/react-query";
import { db } from "./firebase";
import { JobV2 } from "@/app/@types/job";
import { JobBasic } from "@/app/@types/jobBasic";
import { jobV2ToBasic } from "@/app/_helpers/jobBasic";
import { isJobsIndexEnabledFor } from "@/app/_constants/jobsIndex";

// Reads and optimistic patches for the slim jobs index
// (users/{uid}/jobsIndex/{jobUid}, maintained by the syncJobsIndex trigger).
// The index lives in the React Query cache under ["jobsIndex", uid]; the
// realtime listener in useGetJobsIndex overwrites it on every server change,
// so optimistic patches only need to hold until the trigger's write lands
// (1-10s), then the real entry replaces them with identical data.

export const jobsIndexQueryKey = (uid: string) => ["jobsIndex", uid];

// How many jobsIndex listeners (useGetJobsIndex) are open per tenant. The
// cached index is only current while one is: after the board unmounts the
// cache lingers (gcTime 1h) without updates, so readers that want current
// data (dashboard stats) fall back to a fresh read when this is zero.
const liveIndexListeners = new Map<string, number>();
export const markJobsIndexListener = (uid: string, delta: 1 | -1) => {
  const next = (liveIndexListeners.get(uid) ?? 0) + delta;
  if (next > 0) liveIndexListeners.set(uid, next);
  else liveIndexListeners.delete(uid);
};
export const isJobsIndexLive = (uid: string) =>
  (liveIndexListeners.get(uid) ?? 0) > 0;

export const getJobsIndex = async (uid: string): Promise<JobBasic[]> => {
  const snapshot = await getDocs(collection(db, "users", uid, "jobsIndex"));
  return snapshot.docs.map((doc) => doc.data() as JobBasic);
};

// Full-doc fetch for bulk actions in index mode (job card printing needs
// measurements/parts that slim entries don't carry). Chunked to Firestore's
// 30-value "in" limit, same pattern as bulkEditJobTags.
export const getJobsByUids = async (
  uid: string,
  jobUids: string[]
): Promise<JobV2[]> => {
  const results: JobV2[] = [];
  const chunkSize = 30;
  for (let i = 0; i < jobUids.length; i += chunkSize) {
    const chunk = jobUids.slice(i, i + chunkSize);
    const snapshot = await getDocs(
      query(collection(db, "users", uid, "jobs"), where("uid", "in", chunk))
    );
    snapshot.docs.forEach((docSnap) => results.push(docSnap.data() as JobV2));
  }
  return results;
};

// Slim entries for a specific set of jobs (a client's job list, for example),
// for checks that only need what the index carries, such as whether any
// payments exist. Same 30-value "in" chunking as getJobsByUids.
export const getJobsIndexEntriesByUids = async (
  uid: string,
  jobUids: string[]
): Promise<JobBasic[]> => {
  const results: JobBasic[] = [];
  const chunkSize = 30;
  for (let i = 0; i < jobUids.length; i += chunkSize) {
    const chunk = jobUids.slice(i, i + chunkSize);
    const snapshot = await getDocs(
      query(
        collection(db, "users", uid, "jobsIndex"),
        where("uid", "in", chunk)
      )
    );
    snapshot.docs.forEach((docSnap) =>
      results.push(docSnap.data() as JobBasic)
    );
  }
  return results;
};

// Full pending-job fetch for index-mode rows: pending docs never enter the
// index, and Review Booking needs the complete document.
export const getPendingJobByUid = async (
  uid: string,
  jobUid: string
): Promise<JobV2 | null> => {
  const snapshot = await getDoc(doc(db, "users", uid, "pendingJobs", jobUid));
  return snapshot.exists()
    ? { ...(snapshot.data() as JobV2), jobStatus: "pending" as const }
    : null;
};

// Call after our own create/update succeeds so the list reflects it
// immediately instead of after the trigger round-trip.
export const patchJobsIndexEntry = (
  queryClient: QueryClient,
  uid: string,
  job: JobV2
): void => {
  if (!isJobsIndexEnabledFor(uid)) return;
  const basic = jobV2ToBasic(job);
  queryClient.setQueryData<JobBasic[]>(jobsIndexQueryKey(uid), (prev) =>
    prev ? [basic, ...prev.filter((entry) => entry.uid !== basic.uid)] : prev
  );
};

export const removeJobsIndexEntry = (
  queryClient: QueryClient,
  uid: string,
  jobUid: string
): void => {
  if (!isJobsIndexEnabledFor(uid)) return;
  queryClient.setQueryData<JobBasic[]>(jobsIndexQueryKey(uid), (prev) =>
    prev ? prev.filter((entry) => entry.uid !== jobUid) : prev
  );
};
